import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewConfigKeys, reviewCycleKeys, reviewTemplateKeys } from "@/lib/query";
import type { ReviewConfigCreateInput, ReviewConfigUpdateInput } from "@/schemas/review-config";

/** The four review reference lists, as their URL segments. */
export type ReviewConfigList = "statuses" | "cycle-types" | "sentiments" | "relationships";

export interface ReviewConfigRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  /**
   * Statuses only: which stages may use this one. A status can serve several,
   * which is why it is a list. Empty on the other three lists.
   */
  scopes: string[];
  /**
   * Cycle kinds only (WI-314): true when a round of this kind pulls everyone
   * in, false when it starts empty and HR names who it covers. Null on the
   * other three lists.
   */
  coversEveryone: boolean | null;
  sortOrder: number;
  isActive: boolean;
  /** False when the app depends on this row, so the UI can explain itself. */
  isDeletable: boolean;
}

async function messageFrom(res: Response, fallback: string): Promise<string> {
  const json = await res.json().catch(() => null);
  const error = (json as { error?: unknown } | null)?.error;
  return typeof error === "string" && error.length > 0 ? error : fallback;
}

export function useReviewConfig(list: ReviewConfigList) {
  return useQuery({
    queryKey: reviewConfigKeys.list(list),
    queryFn: async (): Promise<ReviewConfigRow[]> => {
      const res = await fetch(`/api/review-config/${list}`);
      if (!res.ok) throw new Error(await messageFrom(res, "Failed to load this list"));
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * All four write paths share one hook: the lists are the same shape, and the
 * server carries the per-list rules (what may be deleted, what is in use), so
 * the client has none to duplicate.
 */
export function useReviewConfigMutations(list: ReviewConfigList) {
  const queryClient = useQueryClient();
  /*
    These four lists are read well beyond their own settings page: the
    relationship types drive the form builder's "Who is asked" section, and the
    statuses label rounds and reviews. Invalidating only this list left a
    renamed row showing its old name on both for up to the 30s stale time, which
    reads as the rename not having worked.
  */
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: reviewConfigKeys.list(list) });
    queryClient.invalidateQueries({ queryKey: reviewTemplateKeys.relationshipTypes });
    queryClient.invalidateQueries({ queryKey: reviewCycleKeys.all });
  };

  const create = useMutation({
    mutationFn: async (input: ReviewConfigCreateInput) => {
      const res = await fetch(`/api/review-config/${list}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await messageFrom(res, "Couldn't add that"));
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast.success("Added");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ReviewConfigUpdateInput }) => {
      const res = await fetch(`/api/review-config/${list}/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error(await messageFrom(res, "Couldn't save that"));
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast.success("Saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/review-config/${list}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await messageFrom(res, "Couldn't remove that"));
    },
    onSuccess: () => {
      invalidate();
      toast.success("Removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const reorder = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const res = await fetch(`/api/review-config/${list}/${id}/reorder`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ direction }),
      });
      if (!res.ok) throw new Error(await messageFrom(res, "Couldn't reorder that"));
    },
    onSuccess: invalidate,
    onError: (error: Error) => toast.error(error.message),
  });

  return { create, update, remove, reorder };
}
